<template>
    <form ref="passwordResetForm" class="form-control flex space-y-4">
        <div class="grid grid-cols-2 gap-3 lg:gap-6">
            <div class="col-span-2 lg:col-span-1">
                <label for="register-password" class="label">
                    <span class="label-text">{{ $t('customer.passwordResetForm.passwordLabel') }}</span>
                </label>
                <div class="join w-full">
                    <input
                        id="register-password"
                        ref="passwordInput"
                        v-model="password"
                        required
                        :type="showPassword ? 'text' : 'password'"
                        :placeholder="$t('customer.passwordResetForm.passwordPlaceholder')"
                        :pattern="PASSWORD_PATTERN.source"
                        class="input input-bordered w-full border-r-0 join-item"
                        @input="validatePassword"
                    >
                    <div
                        role="button"
                        class="btn btn-square btn-ghost border-l-0 border-border hover:border-border join-item"
                        tabindex="0"
                        :aria-pressed="showPassword"
                        :aria-label="showPassword ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                        @click="showPassword = !showPassword"
                        @keydown.space.prevent="showPassword = !showPassword"
                    >
                        <MiscIcon v-if="!showPassword" name="eye" class="w-5 h-5" />
                        <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                    </div>
                </div>
                <div class="label mt-1">
                    <span class="label-text-alt text-sm text-base-content-subtle font-normal leading-5">{{ $t('customer.passwordValidation.passwordInvalid') }}</span>
                </div>
            </div>

            <div class="col-span-2 lg:col-span-1">
                <label for="register-password-repeat" class="label">
                    <span class="label-text">{{ $t('customer.passwordResetForm.passwordRepeatLabel') }}</span>
                </label>
                <div class="join w-full">
                    <input
                        id="register-password-repeat"
                        ref="passwordRepeatInput"
                        v-model="passwordRepeat"
                        required
                        :type="showPassword ? 'text' : 'password'"
                        :placeholder="$t('customer.passwordResetForm.passwordRepeatPlaceholder')"
                        class="input input-bordered w-full border-r-0 join-item"
                    >
                    <div
                        role="button"
                        class="btn btn-square btn-ghost border-l-0 border-border hover:border-border join-item"
                        tabindex="0"
                        :aria-pressed="showPassword"
                        :aria-label="showPassword ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                        @click="showPassword = !showPassword"
                        @keydown.space.prevent="showPassword = !showPassword"
                    >
                        <MiscIcon v-if="!showPassword" name="eye" class="w-5 h-5" />
                        <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                    </div>
                </div>
            </div>
        </div>

        <button
            type="submit"
            class="btn btn-primary btn-lg btn-block"
            :disabled="loading"
            @click.prevent="onResetPasswordClick()"
        >
            <span v-if="loading" class="loading" />
            <span>{{ $t('customer.passwordResetForm.title') }}</span>
        </button>
    </form>
</template>

<script setup lang="ts">
import { ApiClientError } from '@shopware/api-client'

const props = defineProps<{
    hash: string
}>()

const { apiClient } = useShopwareContext()
const { t } = useI18n()
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)
const { validateForm } = formValidation()
const { validatePassword: validatePasswordField, PASSWORD_PATTERN } = usePasswordValidation()
const { showNotification } = useNotification()
const loading = ref(false)

const passwordResetForm = ref()
const password = ref()
const passwordInput = ref()
const passwordRepeat = ref()
const showPassword = ref(false)

function validatePassword () {
    validatePasswordField(passwordInput, password.value)
}

async function onResetPasswordClick () {
    loading.value = true
    const isValid = await validateForm(passwordResetForm.value)

    if (!isValid) {
        loading.value = false
        return
    }

    try {
        await apiClient.invoke('recoveryPassword post /account/recovery-password-confirm', {
            body: {
                hash: props.hash,
                newPassword: password.value,
                newPasswordConfirm: passwordRepeat.value
            }
        })

        password.value = ''
        passwordRepeat.value = ''

        showNotification(t('customer.passwordResetForm.success'), 'success')

        await navigateTo(formatLink('/customer/login'))
    } catch (e: unknown) {
        if (e instanceof ApiClientError) {
            const detail = e.details?.errors?.[0]?.detail
            showNotification(detail ?? e.message, 'error', true)
        } else {
            showNotification(e instanceof Error ? e.message : String(e), 'error', true)
        }
    } finally {
        loading.value = false
    }
}
</script>
